<template>
  <div id="masonry">
    <div
      v-for="component in componentsWithFilters"
      :id="'masonry-brick-' + component.title"
      :key="component.title"
      class="elevation-2 masonry-brick"
    >
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th colspan="3" class="text-left">
                {{ component.title }}
              </th>
            </tr>
            <tr>
              <th class="text-left">
                Name
              </th>
              <th class="text-left">
                Percentage
              </th>
              <th class="text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="value in component.data"
              :key="value.name"
            >
              <td>{{ value.name }}</td>
              <td>{{ value.percentage.toFixed(2) }}%</td>
              <td class="text-center">
                <v-btn
                  small
                  icon
                  :color="value.show ? 'green' : 'red'"
                  @click="toggleHardwareFilter(value.name, component.title)"
                >
                  <v-icon>
                    {{ value.show ? mdiEye : mdiEyeOff }}
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
    <input-methods-chart
      id="masonry-brick-input-methods"
      class="elevation-2 masonry-brick"
      :loading="loadingInputMethods"
      :series="inputMethodsSeries"
      :categories="inputMethodsCategories"
    />
  </div>
</template>

<script>
import { mdiEye, mdiEyeOff } from '@mdi/js';
import InputMethodsChart from './inputMethodsChart.vue';
export default {
  components: {
    InputMethodsChart
  },
  props: {
    filters: {
      required: true,
      type: Array
    }
  },
  data () {
    return {
      mdiEye,
      mdiEyeOff,
      loading: false,
      loadingInputMethods: false,
      components: [],
      inputMethodsSeries: [],
      inputMethodsCategories: []
    }
  },
  async mounted () {
    await Promise.all([
      this.getData(),
      this.getInputhMethods()
    ])
  },
  computed: {
    componentsWithFilters () {
      return this.components.map(val => {
        const filters = this.filters.filter(f => f.category === val.title)
        return {
          ...val,
          data: val.data.map(v => {
            if (filters.some(f => f.value.toString() === v.name.toString())) {
              return {
                ...v,
                show: false
              }
            } else {
              return {
                ...v,
                show: true
              }
            }
          })
        }
      })
    }
  },
  watch: {
    async '$route.query' () {
      await Promise.all([
        this.getData(),
        this.getInputhMethods()
      ])
    },
    async components (val) {
      await this.updateMasonryContent(val)
    },
    async inputMethodsCategories () {
      await this.updateMasonryContent()
    }
  },
  methods: {
    async getData () {
      if (!this.$route.query.build_id) {
        return
      }

      this.loading = true
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/telemetry/hardware/statistic`, {
          params: {
            build_id: this.$route.query.build_id,
            user_id: this.$route.query.user_id
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.components = data
      }
      this.loading = false
    },
    async getInputhMethods () {
      if (!this.$route.query.build_id) {
        return
      }

      this.loadingInputMethods = true
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/telemetry/hardware/input-methods`, {
          params: {
            build_id: this.$route.query.build_id,
            user_id: this.$route.query.user_id
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.inputMethodsCategories = data.categories
        this.inputMethodsSeries = data.series
      }
      this.loadingInputMethods = false
    },
    async toggleHardwareFilter (name, category) {
      const filter = this.filters.find(f => f.value.toString() === name.toString() && f.category === category)

      if (filter) {
        await this.deleteFilter(filter.id)
      } else {
        await this.createFilter({ value: name, category })
      }
    },
    async createFilter(value) {
      const { data } = await this.$axios.post(this.$store.getters.projectPath + '/telemetry/hardware/filters', value)
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.$emit('update-filters', [...this.filters, data])
      }
    },
    async deleteFilter(id) {
      const { data } = await this.$axios.delete(this.$store.getters.projectPath + '/telemetry/hardware/filters/' + id)
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.$emit('update-filters', this.filters.filter(f => f.id !== id))
      }
    },
    async updateMasonryContent () {
      await this.$nextTick()
      const grid = document.getElementById('masonry')
      const rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap'))
      const rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'))
      for (const component of [ ...this.componentsWithFilters, { title: 'input-methods' } ]) {
        const item = document.getElementById('masonry-brick-' + component.title)
        if (item !== null) {
          const rowSpan = Math.ceil((item.clientHeight + rowGap) / (rowHeight + rowGap))
          item.style.gridRowEnd = 'span ' + rowSpan
        }
      }
    },
  }
}
</script>

<style scoped>
  #masonry {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: repeat(auto-fill, minmax(470px,1fr));
    grid-auto-rows: 0;
  }

  .masonry-brick {
    height: fit-content;
  }
</style>